/**
 * Adjacency Map API Client — replaces LocalJsonProvider.
 *
 * Implements the DataService interface using platform REST API.
 * Handles snake_case ↔ camelCase transformation between API and frontend types.
 *
 * WQ-097: Copy-then-adapt migration
 */

import type {
  AdjacencyNode,
  CreateNodeInput,
  UpdateNodeInput,
  ConnectionEdge,
} from '../components/adjacency-map/types/adjacency';

// ── Platform Auth Integration ────────────────────────────────────────

type AuthFetchFn = (url: string, options?: RequestInit) => Promise<Response>;

let _authFetch: AuthFetchFn | null = null;
let _activeTenantId: string | null = null;

/** Set the platform authFetch function. Must be called before any API calls. */
export function setAuthFetch(fn: AuthFetchFn): void {
  _authFetch = fn;
}

/** Set the active tenant ID for tenant-context scoping. */
export function setActiveTenantId(id: string | null): void {
  _activeTenantId = id;
}

async function apiFetch(path: string, options?: RequestInit) {
  if (!_authFetch) {
    throw new Error('Auth not initialized — call setAuthFetch() first');
  }

  // Append tenant_id query param when in tenant context (WQ-207 snake_case)
  let url = path;
  if (_activeTenantId) {
    const sep = path.includes('?') ? '&' : '?';
    url = `${path}${sep}tenant_id=${_activeTenantId}`;
  }

  const res = await _authFetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`API ${res.status}: ${body}`);
  }
  if (res.status === 204) return null;
  return res.json();
}

// ── API → Frontend Transform ────────────────────────────────────────

interface ApiEdge {
  id: string;
  source_node_id: string;
  target_node_id: string;
  relationship_type: string;
  line_style: string;
  line_color: string;
  label: string | null;
}

interface ApiNode {
  id: string;
  map_id: string;
  tenant_id: string;
  name: string;
  description: string;
  node_type: string;
  source: string;
  strategic_initiative: string;
  adjacency_axis: string;
  relatedness_ring: number;
  shared_customers: number;
  shared_costs: number;
  common_competitors: number;
  attractiveness_score: number;
  reinforces_core: boolean;
  phase: string | null;
  status: string | null;
  priority: number | null;
  map_position_x: number | null;
  map_position_y: number | null;
  est_rnd: number | null;
  est_revenue: number | null;
  i_rnd: number | null;
  f_rnd: number | null;
  total_budget_estimate: number | null;
  tam: number | null;
  sam: number | null;
  tam_ai_suggested: boolean | null;
  sam_ai_suggested: boolean | null;
  start_date: string | null;
  end_date: string | null;
  mrd_date: string | null;
  mrd_target_date: string | null;
  atd_date: string | null;
  atq_date: string | null;
  ats_date: string | null;
  gantt_start_date: string | null;
  gantt_end_date: string | null;
  fte_allocation: number | null;
  iwt_south: boolean;
  iwt_south_fte: number | null;
  is_mvp: boolean;
  sp_list_item_id: number | null;
  last_synced_at: string | null;
  sp_priority_tag: string | null;
  sp_sync_review_status: string | null;
  dev_lane: string | null;
  dev_team: string | null;
  product_manager: string | null;
  product_line: string | null;
  budget_project_name: string | null;
  created_at: string;
  updated_at: string;
  edges: ApiEdge[];
}

/** Transform API edge to frontend ConnectionEdge. sourceNodeId is the owning node. */
function apiEdgeToConnectionEdge(edge: ApiEdge, sourceNodeId: string): ConnectionEdge {
  return {
    targetId: edge.source_node_id === sourceNodeId ? edge.target_node_id : edge.source_node_id,
    relationship: edge.relationship_type as ConnectionEdge['relationship'],
    lineStyle: edge.line_style as ConnectionEdge['lineStyle'],
    lineColor: edge.line_color,
    label: edge.label ?? undefined,
    edgeId: edge.id,
  };
}

/** Transform API node to frontend AdjacencyNode. */
function apiNodeToFrontend(n: ApiNode): AdjacencyNode {
  return {
    id: n.id,
    name: n.name,
    description: n.description,
    strategicInitiative: n.strategic_initiative as AdjacencyNode['strategicInitiative'],
    phase: n.phase as AdjacencyNode['phase'],
    status: n.status as AdjacencyNode['status'],
    priority: n.priority,
    adjacencyAxis: n.adjacency_axis as AdjacencyNode['adjacencyAxis'],
    relatednessRing: n.relatedness_ring as AdjacencyNode['relatednessRing'],
    sharedCustomers: n.shared_customers,
    sharedCosts: n.shared_costs,
    commonCompetitors: n.common_competitors,
    attractivenessScore: n.attractiveness_score,
    reinforcesCore: n.reinforces_core,
    nodeType: n.node_type as AdjacencyNode['nodeType'],
    source: n.source as AdjacencyNode['source'],
    edges: (n.edges ?? []).map((e) => apiEdgeToConnectionEdge(e, n.id)),
    parentIds: (n.edges ?? [])
      .filter((e) => e.relationship_type === 'parent' && e.source_node_id === n.id)
      .map((e) => e.target_node_id),
    mapPosition:
      n.map_position_x != null && n.map_position_y != null
        ? { x: n.map_position_x, y: n.map_position_y }
        : null,
    spPriorityTag: n.sp_priority_tag,
    spSyncReviewStatus: n.sp_sync_review_status as AdjacencyNode['spSyncReviewStatus'],
    spListItemId: n.sp_list_item_id,
    lastSyncedAt: n.last_synced_at,
    createdAt: n.created_at,
    modifiedAt: n.updated_at,
    spSyncSnapshot: null, // Not returned by API
    estRnD: n.est_rnd,
    estRevenue: n.est_revenue,
    iRnD: n.i_rnd,
    fRnD: n.f_rnd,
    totalBudgetEstimate: n.total_budget_estimate,
    tam: n.tam,
    sam: n.sam,
    tamAiSuggested: n.tam_ai_suggested,
    samAiSuggested: n.sam_ai_suggested,
    startDate: n.start_date,
    endDate: n.end_date,
    mrdDate: n.mrd_date,
    mrdTargetDate: n.mrd_target_date,
    atdDate: n.atd_date,
    atqDate: n.atq_date,
    atsDate: n.ats_date,
    ganttStartDate: n.gantt_start_date,
    ganttEndDate: n.gantt_end_date,
    fteAllocation: n.fte_allocation,
    iwtSouth: n.iwt_south,
    iwtSouthFte: n.iwt_south_fte,
    isMvp: n.is_mvp,
    devLane: n.dev_lane,
    devTeam: n.dev_team,
    productManager: n.product_manager,
    productLine: n.product_line,
    budgetProjectName: n.budget_project_name,
  };
}

/** Transform frontend node input to API create request. */
function frontendToApiCreate(input: CreateNodeInput): Record<string, unknown> {
  return {
    name: input.name,
    description: input.description ?? '',
    node_type: input.nodeType,
    source: input.source,
    strategic_initiative: input.strategicInitiative,
    adjacency_axis: input.adjacencyAxis,
    relatedness_ring: input.relatednessRing,
    shared_customers: input.sharedCustomers,
    shared_costs: input.sharedCosts,
    common_competitors: input.commonCompetitors,
    attractiveness_score: input.attractivenessScore,
    reinforces_core: input.reinforcesCore,
    phase: input.phase,
    status: input.status,
    priority: input.priority,
    map_position_x: input.mapPosition?.x ?? null,
    map_position_y: input.mapPosition?.y ?? null,
    est_rnd: input.estRnD,
    est_revenue: input.estRevenue,
    i_rnd: input.iRnD,
    f_rnd: input.fRnD,
    total_budget_estimate: input.totalBudgetEstimate,
    tam: input.tam,
    sam: input.sam,
    tam_ai_suggested: input.tamAiSuggested,
    sam_ai_suggested: input.samAiSuggested,
    start_date: input.startDate,
    end_date: input.endDate,
    mrd_date: input.mrdDate,
    mrd_target_date: input.mrdTargetDate,
    atd_date: input.atdDate,
    atq_date: input.atqDate,
    ats_date: input.atsDate,
    gantt_start_date: input.ganttStartDate,
    gantt_end_date: input.ganttEndDate,
    fte_allocation: input.fteAllocation,
    iwt_south: input.iwtSouth,
    iwt_south_fte: input.iwtSouthFte,
    is_mvp: input.isMvp,
    sp_list_item_id: input.spListItemId,
    last_synced_at: input.lastSyncedAt,
    sp_priority_tag: input.spPriorityTag,
    sp_sync_review_status: input.spSyncReviewStatus,
    dev_lane: input.devLane,
    dev_team: input.devTeam,
    product_manager: input.productManager,
    product_line: input.productLine,
    budget_project_name: input.budgetProjectName,
  };
}

/** Transform frontend partial update to API update request. */
function frontendToApiUpdate(changes: UpdateNodeInput): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  if (changes.name !== undefined) result.name = changes.name;
  if (changes.description !== undefined) result.description = changes.description;
  if (changes.nodeType !== undefined) result.node_type = changes.nodeType;
  if (changes.source !== undefined) result.source = changes.source;
  if (changes.strategicInitiative !== undefined) result.strategic_initiative = changes.strategicInitiative;
  if (changes.adjacencyAxis !== undefined) result.adjacency_axis = changes.adjacencyAxis;
  if (changes.relatednessRing !== undefined) result.relatedness_ring = changes.relatednessRing;
  if (changes.sharedCustomers !== undefined) result.shared_customers = changes.sharedCustomers;
  if (changes.sharedCosts !== undefined) result.shared_costs = changes.sharedCosts;
  if (changes.commonCompetitors !== undefined) result.common_competitors = changes.commonCompetitors;
  if (changes.attractivenessScore !== undefined) result.attractiveness_score = changes.attractivenessScore;
  if (changes.reinforcesCore !== undefined) result.reinforces_core = changes.reinforcesCore;
  if (changes.phase !== undefined) result.phase = changes.phase;
  if (changes.status !== undefined) result.status = changes.status;
  if (changes.priority !== undefined) result.priority = changes.priority;
  if (changes.mapPosition !== undefined) {
    result.map_position_x = changes.mapPosition?.x ?? null;
    result.map_position_y = changes.mapPosition?.y ?? null;
  }
  if (changes.estRnD !== undefined) result.est_rnd = changes.estRnD;
  if (changes.estRevenue !== undefined) result.est_revenue = changes.estRevenue;
  if (changes.iRnD !== undefined) result.i_rnd = changes.iRnD;
  if (changes.fRnD !== undefined) result.f_rnd = changes.fRnD;
  if (changes.totalBudgetEstimate !== undefined) result.total_budget_estimate = changes.totalBudgetEstimate;
  if (changes.tam !== undefined) result.tam = changes.tam;
  if (changes.sam !== undefined) result.sam = changes.sam;
  if (changes.tamAiSuggested !== undefined) result.tam_ai_suggested = changes.tamAiSuggested;
  if (changes.samAiSuggested !== undefined) result.sam_ai_suggested = changes.samAiSuggested;
  if (changes.startDate !== undefined) result.start_date = changes.startDate;
  if (changes.endDate !== undefined) result.end_date = changes.endDate;
  if (changes.mrdDate !== undefined) result.mrd_date = changes.mrdDate;
  if (changes.mrdTargetDate !== undefined) result.mrd_target_date = changes.mrdTargetDate;
  if (changes.atdDate !== undefined) result.atd_date = changes.atdDate;
  if (changes.atqDate !== undefined) result.atq_date = changes.atqDate;
  if (changes.atsDate !== undefined) result.ats_date = changes.atsDate;
  if (changes.ganttStartDate !== undefined) result.gantt_start_date = changes.ganttStartDate;
  if (changes.ganttEndDate !== undefined) result.gantt_end_date = changes.ganttEndDate;
  if (changes.fteAllocation !== undefined) result.fte_allocation = changes.fteAllocation;
  if (changes.iwtSouth !== undefined) result.iwt_south = changes.iwtSouth;
  if (changes.iwtSouthFte !== undefined) result.iwt_south_fte = changes.iwtSouthFte;
  if (changes.isMvp !== undefined) result.is_mvp = changes.isMvp;
  if (changes.devLane !== undefined) result.dev_lane = changes.devLane;
  if (changes.devTeam !== undefined) result.dev_team = changes.devTeam;
  if (changes.productManager !== undefined) result.product_manager = changes.productManager;
  if (changes.productLine !== undefined) result.product_line = changes.productLine;
  if (changes.budgetProjectName !== undefined) result.budget_project_name = changes.budgetProjectName;
  return result;
}

// ── Diff engine (WQ-340) ─────────────────────────────────────────────
//
// Undo, redo and Bulk Edit persist by diffing a target snapshot against FRESH
// server state (never the client store) and issuing only the calls needed.
// Edges are keyed by (source, target, relationship) — not by edge id — so
// snapshots taken before edge ids existed still diff correctly.

export type EdgePairKind = 'parent-child' | 'peer';

export interface EdgeAttrs {
  lineStyle: ConnectionEdge['lineStyle'];
  lineColor: string;
  label?: string;
}

export interface EdgeRow extends EdgeAttrs {
  sourceId: string;
  targetId: string;
  relationship: ConnectionEdge['relationship'];
  edgeId?: string;
}

export interface SnapshotPlan {
  createNodes: AdjacencyNode[];
  patchNodes: Array<{ id: string; fields: Record<string, unknown> }>;
  deleteEdges: EdgeRow[];
  createEdges: EdgeRow[];
  patchEdges: Array<{ edgeId: string; attrs: EdgeAttrs }>;
  deleteNodes: string[];
}

const edgeKey = (r: Pick<EdgeRow, 'sourceId' | 'targetId' | 'relationship'>) =>
  `${r.sourceId}|${r.targetId}|${r.relationship}`;

/** Every edge row a node list holds. Each node carries the rows where it is the source. */
export function edgeRows(nodes: AdjacencyNode[]): Map<string, EdgeRow> {
  const rows = new Map<string, EdgeRow>();
  for (const n of nodes) {
    for (const e of n.edges) {
      const row: EdgeRow = {
        sourceId: n.id, targetId: e.targetId, relationship: e.relationship,
        lineStyle: e.lineStyle, lineColor: e.lineColor, label: e.label, edgeId: e.edgeId,
      };
      rows.set(edgeKey(row), row);
    }
  }
  return rows;
}

const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/**
 * API fields of `target` that differ from `current`. A position counts as changed
 * only when x or y differs; then both coordinates are sent together.
 */
export function changedNodeFields(current: AdjacencyNode, target: AdjacencyNode): Record<string, unknown> {
  const now = frontendToApiUpdate(current as unknown as UpdateNodeInput);
  const next = frontendToApiUpdate(target as unknown as UpdateNodeInput);
  const fields: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(next)) {
    if (key === 'map_position_x' || key === 'map_position_y') continue;
    if (!same(value, now[key])) fields[key] = value;
  }
  if (!same(next.map_position_x, now.map_position_x) || !same(next.map_position_y, now.map_position_y)) {
    fields.map_position_x = next.map_position_x;
    fields.map_position_y = next.map_position_y;
  }
  return fields;
}

/** The minimal set of calls that turns `current` (server truth) into `target`. */
export function planSnapshot(current: AdjacencyNode[], target: AdjacencyNode[]): SnapshotPlan {
  const currentById = new Map(current.map((n) => [n.id, n]));
  const targetIds = new Set(target.map((n) => n.id));
  const removed = new Set(current.filter((n) => !targetIds.has(n.id)).map((n) => n.id));
  const plan: SnapshotPlan = {
    createNodes: target.filter((n) => !currentById.has(n.id)),
    patchNodes: [],
    deleteEdges: [],
    createEdges: [],
    patchEdges: [],
    deleteNodes: [...removed],
  };
  for (const t of target) {
    const c = currentById.get(t.id);
    if (!c) continue;
    const fields = changedNodeFields(c, t);
    if (Object.keys(fields).length > 0) plan.patchNodes.push({ id: t.id, fields });
  }

  const now = edgeRows(current);
  const next = edgeRows(target);
  for (const [key, row] of now) {
    // Rows touching a removed node go with it: the server cascades on node delete.
    if (!next.has(key) && !removed.has(row.sourceId) && !removed.has(row.targetId)) plan.deleteEdges.push(row);
  }
  for (const [key, row] of next) {
    const existing = now.get(key);
    if (!existing) {
      if (targetIds.has(row.targetId)) plan.createEdges.push(row);
    } else if (
      existing.edgeId &&
      !same([existing.lineStyle, existing.lineColor, existing.label], [row.lineStyle, row.lineColor, row.label])
    ) {
      plan.patchEdges.push({ edgeId: existing.edgeId, attrs: { lineStyle: row.lineStyle, lineColor: row.lineColor, label: row.label } });
    }
  }
  return plan;
}

const PAIR_ROWS: Record<EdgePairKind, [ConnectionEdge['relationship'], ConnectionEdge['relationship']]> = {
  'parent-child': ['child', 'parent'],
  peer: ['peer', 'peer'],
};

/** Accept an export file (`{metadata, nodes}`) or a bare node array; return the node list. */
export function importFileNodes(file: unknown): unknown[] {
  if (Array.isArray(file)) return file;
  if (file && typeof file === 'object' && Array.isArray((file as { nodes?: unknown }).nodes)) {
    return (file as { nodes: unknown[] }).nodes;
  }
  throw new Error('Expected an exported adjacency map: a JSON array of nodes or {"nodes": [...]}');
}

// ── API Client (implements DataService interface shape) ─────────────

export class AdjacencyMapApiProvider {
  private base: string;

  constructor(private mapId: string) {
    this.base = '/api/admin/adjacency-maps';
  }

  setMapId(id: string) {
    this.mapId = id;
  }

  // DataService interface methods

  async getAll(): Promise<AdjacencyNode[]> {
    const data = await apiFetch(`${this.base}/${this.mapId}/nodes`);
    return (data.nodes ?? data).map(apiNodeToFrontend);
  }

  async getById(id: string): Promise<AdjacencyNode | null> {
    try {
      const data = await apiFetch(`${this.base}/${this.mapId}/nodes/${id}`);
      return apiNodeToFrontend(data);
    } catch {
      return null;
    }
  }

  async create(input: CreateNodeInput, id?: string): Promise<AdjacencyNode> {
    const data = await apiFetch(`${this.base}/${this.mapId}/nodes`, {
      method: 'POST',
      body: JSON.stringify(id ? { ...frontendToApiCreate(input), id } : frontendToApiCreate(input)),
    });
    return apiNodeToFrontend(data);
  }

  async update(id: string, changes: UpdateNodeInput): Promise<AdjacencyNode> {
    // Handle edges separately — they're managed via edge endpoints
    const { edges, parentIds, ...scalarChanges } = changes;
    const apiChanges = frontendToApiUpdate(scalarChanges as UpdateNodeInput);

    // Only call PATCH if there are scalar changes
    if (Object.keys(apiChanges).length > 0) {
      const data = await apiFetch(`${this.base}/${this.mapId}/nodes/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(apiChanges),
      });
      return apiNodeToFrontend(data);
    }

    // If only edges changed, return current state
    const current = await this.getById(id);
    return current!;
  }

  async delete(id: string): Promise<void> {
    await apiFetch(`${this.base}/${this.mapId}/nodes/${id}`, {
      method: 'DELETE',
    });
  }

  async search(query: string): Promise<AdjacencyNode[]> {
    const data = await apiFetch(
      `${this.base}/${this.mapId}/nodes/search?q=${encodeURIComponent(query)}`
    );
    return (data.nodes ?? data).map(apiNodeToFrontend);
  }

  // Edge-specific methods

  async createEdge(sourceNodeId: string, targetNodeId: string, relationship: string, lineStyle = 'solid', lineColor = '#3b82f6', label?: string): Promise<string> {
    const data = await apiFetch(`${this.base}/${this.mapId}/edges`, {
      method: 'POST',
      body: JSON.stringify({
        source_node_id: sourceNodeId,
        target_node_id: targetNodeId,
        relationship_type: relationship,
        line_style: lineStyle,
        line_color: lineColor,
        label: label ?? null,
      }),
    });
    return data.id;
  }

  async deleteEdge(edgeId: string): Promise<void> {
    await apiFetch(`${this.base}/${this.mapId}/edges/${edgeId}`, {
      method: 'DELETE',
    });
  }

  async updateEdge(edgeId: string, attrs: EdgeAttrs): Promise<void> {
    await apiFetch(`${this.base}/${this.mapId}/edges/${edgeId}`, {
      method: 'PATCH',
      body: JSON.stringify({ line_style: attrs.lineStyle, line_color: attrs.lineColor, label: attrs.label ?? null }),
    });
  }

  /**
   * Create a connection as its mirrored pair of rows (parent-child: `child` on A
   * plus `parent` on B; peer: `peer` both ways). If the second row fails, the
   * first is removed so the map never holds a one-sided edge.
   */
  async createEdgePair(a: string, b: string, kind: EdgePairKind, attrs: EdgeAttrs): Promise<void> {
    const [forward, back] = PAIR_ROWS[kind];
    const firstId = await this.createEdge(a, b, forward, attrs.lineStyle, attrs.lineColor, attrs.label);
    try {
      await this.createEdge(b, a, back, attrs.lineStyle, attrs.lineColor, attrs.label);
    } catch (e) {
      await this.deleteEdge(firstId).catch(() => undefined);
      throw e;
    }
  }

  /** Server-side edge rows between two nodes, in both directions (with ids). */
  private async rowsBetween(a: string, b: string): Promise<EdgeRow[]> {
    const [nodeA, nodeB] = await Promise.all([this.getById(a), this.getById(b)]);
    return [...edgeRows([nodeA, nodeB].filter((n): n is AdjacencyNode => n !== null)).values()]
      .filter((r) => (r.sourceId === a && r.targetId === b) || (r.sourceId === b && r.targetId === a));
  }

  /** Delete every edge row between two nodes, both directions, by server id. */
  async deleteEdgesBetween(a: string, b: string): Promise<void> {
    for (const row of await this.rowsBetween(a, b)) {
      if (row.edgeId) await this.deleteEdge(row.edgeId);
    }
  }

  /** Restyle a connection: PATCH both rows of the pair. */
  async updateEdgePair(a: string, b: string, attrs: EdgeAttrs): Promise<void> {
    for (const row of await this.rowsBetween(a, b)) {
      if (row.edgeId) await this.updateEdge(row.edgeId, attrs);
    }
  }

  /**
   * Persist a whole-map snapshot (undo, redo, Bulk Edit) as the minimal diff
   * against fresh server state. Order: create nodes (with their ids) → patch
   * nodes → delete edges → create edges → patch edges → delete nodes.
   */
  async applySnapshot(target: AdjacencyNode[]): Promise<SnapshotPlan> {
    const plan = planSnapshot(await this.getAll(), target);
    for (const node of plan.createNodes) {
      await this.create(node as unknown as CreateNodeInput, node.id);
    }
    for (const { id, fields } of plan.patchNodes) {
      await apiFetch(`${this.base}/${this.mapId}/nodes/${id}`, { method: 'PATCH', body: JSON.stringify(fields) });
    }
    for (const row of plan.deleteEdges) {
      if (row.edgeId) await this.deleteEdge(row.edgeId);
    }
    for (const row of plan.createEdges) {
      await this.createEdge(row.sourceId, row.targetId, row.relationship, row.lineStyle, row.lineColor, row.label);
    }
    for (const { edgeId, attrs } of plan.patchEdges) {
      await this.updateEdge(edgeId, attrs);
    }
    for (const id of plan.deleteNodes) {
      await this.delete(id);
    }
    return plan;
  }

  /** Read-only `GET /export` (the standalone/export format). */
  async exportMap(): Promise<unknown> {
    return apiFetch(`${this.base}/${this.mapId}/export`);
  }

  /**
   * Map → Import data: a deliberate FULL REPLACE through `/import` (export format).
   * A backup of the current map is exported and handed to `saveBackup` first; if
   * that export fails, nothing is imported.
   */
  async importReplace(file: unknown, saveBackup: (backup: unknown, filename: string) => void): Promise<void> {
    const nodes = importFileNodes(file);
    const backup = await this.exportMap();
    const stamp = new Date().toISOString().slice(0, 16).replace(':', '-');
    saveBackup(backup, `adjacency-map-before-import-${stamp}.json`);
    await apiFetch(`${this.base}/${this.mapId}/import`, {
      method: 'POST',
      body: JSON.stringify({ nodes }),
    });
  }

  // Map-level operations

  static async listMaps(): Promise<Array<{ id: string; name: string; node_count: number }>> {
    const data = await apiFetch('/api/admin/adjacency-maps');
    return data.maps ?? data;
  }

  static async createMap(name: string): Promise<{ id: string; name: string }> {
    return apiFetch('/api/admin/adjacency-maps', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  // Resource config

  async getConfig(): Promise<Record<string, unknown>> {
    return apiFetch(`${this.base}/${this.mapId}/config`);
  }

  async updateConfig(config: Record<string, unknown>): Promise<Record<string, unknown>> {
    return apiFetch(`${this.base}/${this.mapId}/config`, {
      method: 'PATCH',
      body: JSON.stringify(config),
    });
  }
}
